import { t } from 'i18next';
import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router-dom';
import queryString from 'query-string';
import { Loader } from '~/components';
import { generateNameFormatted } from '~/helpers/titleHelper';
import { AnalysisLayout } from '~/pages/Analysis/AnalysisLayout';
import { Headers2 } from '~/pages/Analysis/Header';
import {
  BottomLayer, Container, ContainerLoader,
} from '../Predictive/style';
import { getUserProfile } from 'helpers/userProfile';

type TabBase = {
  namePage: string;
  url: string;
};

const TABS_BASE: (TabBase & { clientId: number })[] = [
  {
    clientId: 152, // Santander
    namePage: 'dashboard',
    url: 'https://app.powerbi.com/view?r=eyJrIjoiNTlhYmM3ZjUtM2Q3Zi00ODczLThiNzAtYzk3Zjc1ZWQ2NTMzIiwidCI6IjNlNmM3M2UzLTkxYzctNGQ2Yy1iZjc1LWNjZDUwOThmM2JhMyJ9',
  },
];

export const WaterDashboard: React.FC = () => {
  const [isReady, setIsReady] = useState(false);
  const [url, setUrl] = useState('');
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [hasError, setHasError] = useState(false);
  const [profile] = useState(getUserProfile);

  const history = useHistory();
  const queryPars = queryString.parse(history.location.search);
  const linkBase = history.location.pathname;

  const titles = useMemo(
    () => ({
      dashboard: t('consumoAgua'),
    }),
    [t],
  );

  const filteredTabs = TABS_BASE.filter((tab) => profile.permissions.PER_CLIENT.some((item) => item.clientId === tab.clientId));
  const tabsRefs = useMemo(() => filteredTabs.map(() => React.createRef<HTMLAnchorElement>()), []);

  const currentType = (typeof queryPars.type === 'string' && queryPars.type) || filteredTabs[0]?.namePage;

  const allTabs = useMemo(() => filteredTabs.map((tab, idx) => {
    const link = `${linkBase}?${queryString.stringify({
      ...queryPars,
      type: tab.namePage,
    })}`;
    const title = titles[tab.namePage as keyof typeof titles] ?? tab.namePage;
    return {
      title,
      label: title,
      link,
      isActive: currentType === tab.namePage,
      visible: true,
      ref: tabsRefs[idx],
      url: tab.url,
    };
  }),
  [currentType, linkBase, queryPars, tabsRefs, titles]);

  const activeTab = allTabs.find((tab) => tab.isActive) ?? allTabs[0];

  const handleIframeError = () => {
    setHasError(true);
    setIsReady(false);
  };

  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    const failTimer = setTimeout(() => {
      setHasError(true);
    }, 5000);
    timeoutRef.current = failTimer;
    setUrl(activeTab?.url ?? '');
    setIsReady(false);
  }, [currentType]);

  const handleIframeLoad = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setIsReady(true), 5000);
  };

  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  return (
    <>
      <Helmet>
        <title>{generateNameFormatted('Celsius 360', t('agua'))}</title>
      </Helmet>
      <AnalysisLayout />
      <div style={{ paddingTop: '10px' }}>
        <Headers2 links={allTabs} />
      </div>
      <Container>
        <iframe
          key={url}
          ref={iframeRef}
          title="Dashboard Diel"
          src={url}
          width="100%"
          height="100%"
          style={{
            border: 'none',
            transform: 'scale(1.01)',
            transformOrigin: 'top center',
            display: hasError ? 'none' : isReady ? 'block' : 'none',
          }}
          onLoad={handleIframeLoad}
          onError={handleIframeError}
          onContextMenu={(e) => e.preventDefault()}
        />
        {isReady && (
          <BottomLayer />
        )}
        {!isReady && (
          <ContainerLoader>
            <Loader />
            <p>{t('carregandoDados')}</p>
          </ContainerLoader>
        )}
        {hasError && (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <p>{t('erroRelatorio')}</p>
          </div>
        )}
      </Container>
    </>
  );
};
